import React, { useEffect } from "react";
import { fetchAdvertisements, fetchCampaign } from "actions";
import i18n from "I18n";
import {
  fetchLoginPopCounter,
  hideLeaderboardPopup,
  hidePopUpAll,
  showRefferalRedeenSuccessPopUp,
  showThemeLayout,
} from "redux/reducers/layoutReducer";
import { setUserTeam } from "redux/reducers/teamReducer";
import { fetchTranslations } from "redux/reducers/uiReducer";
import { SdkContext } from "providers/client-sdk-providers";
import { SocketContext } from "providers/SocketProvider";
import store, { type RootState, useAppDispatch, useAppSelector } from "redux/Store";
import { decodeDataFromURL, isStringEqual } from "utils";
import { isKlook } from "utils/applicationSlug";
import axios from "axios";
import { useMediaQuery } from "react-responsive";
import { useLocation } from "react-router-dom";
import api from "actions/api";
import { toggleGameListPopup } from "redux/reducers/modalReducer";
import { APP_SLUGS } from "utils/constants";
import { fetchBalance } from "redux/reducers/statusPointReducer";

export const useMain = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { sessionSocket, initiateSessionSocket } = React.useContext(SocketContext);
  const sdk = React.useContext(SdkContext);
  const { translations, application, user, popUp, gamelistpopup } = useSelectors();
  const isGuestUser = application?.applicationsetting?.enable_guest_user && user?.extra_data?.guest;
  const isDesktop = useMediaQuery({ query: "(min-width: 1022px)" });
  useEffect(
    () => handleSocketConnections(user, sessionSocket, initiateSessionSocket),
    [user, sessionSocket, initiateSessionSocket],
  );
  useEffect(() => handlePhoneNumberRetrieval(user, application, sdk), [sdk, user, application]);
  useEffect(
    () => fetchUserTeamData(user, application, dispatch, sdk),
    [application, application?.applicationsetting?.team_base_application, dispatch, sdk, user],
  );
  useEffect(() => getTranslations(dispatch), [dispatch]);
  useEffect(() => setTranslations(translations), [translations]);
  useEffect(() => handleAdvertisementFetching(dispatch), [dispatch]);
  useEffect(() => handleIOS(), []);
  useEffect(() => handleReferral({ isGuestUser, application, user }), [isGuestUser, application, user]);
  useEffect(() => fetchEthereumData(application), [application]);
  useEffect(
    () => handleGuestUserLoginPopup(application?.applicationsetting?.enable_guest_user, dispatch, application),
    [application, dispatch],
  );
  useEffect(() => handleSocialMediaAuth(user), [user]);
  useEffect(() => adjustUILayout(application, dispatch), [application, dispatch, isDesktop]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => handleLeaderboardPopup(popUp, gamelistpopup, dispatch), [dispatch, isDesktop]);
  useEffect(() => handleLeaderboardPopupLocation(location, dispatch), [dispatch, location, location.pathname]);
  useEffect(() => {
    const { handleTouchStart, handleTouchMove } = preventHorizontalScrollOnTouch();
    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
    };
  }, [application?.slug]);

  return null;
};

// Redefine useSelectors with explicit return type
function useSelectors() {
  return {
    translations: useAppSelector((state: RootState) => state.ui.translations),
    application: useAppSelector((state: RootState) => state.common.application),
    user: useAppSelector((state: RootState) => state.common.user),
    popUp: useAppSelector((state: RootState) => state.layout.popUp),
    gamelistpopup: useAppSelector((state: RootState) => state.modal.gamelistpopup),
  };
}

// Define handlers as functions with appropriate parameters and types
function handleSocketConnections(user: any, sessionSocket: any, initiateSessionSocket: Function) {
  if (sessionSocket === null) {
    initiateSessionSocket();
  }
  if (user !== null) {
    sessionSocket?.emit("login", { userId: user?.id });
  }
}

function handlePhoneNumberRetrieval(user: any, application: any, sdk: any) {
  if (user && application) {
    if (!user.msisdn) {
      if (sdk.getPhoneNumber) {
        sdk.getPhoneNumber().then((value: any) => {
          if (value) {
            api
              .post(`tournaments/users/msisdn/${application?.slug}/`, {
                msisdn: value.phone,
                sign: value.sign,
              })
              .then();
          }
        });
      }
    }
  }
}

function fetchUserTeamData(user: any, application: any, dispatch: any, sdk: any) {
  if (application) {
    if (user) {
      if (application?.applicationsetting?.team_base_application) {
        api
          .get(`tournaments/team-based-application/teams/${application?.slug}/check/`)
          .then(response => {
            if (response.data) {
              if (response?.data?.memmber_of) {
                dispatch(setUserTeam(response?.data?.memmber_of) as any);
              } else {
                dispatch(setUserTeam(null) as any);
              }
            }
          })
          .catch(() => {
            dispatch(setUserTeam(null) as any);
          });
      }
      setTimeout(() => {
        dispatch(fetchCampaign() as any);
      }, 2000);
      if (sdk.postInitCallback) sdk.postInitCallback(user);
    }
  }
}

function getTranslations(dispatch: any) {
  const { country, app_slug, lang } = decodeDataFromURL();
  if (app_slug && lang && isStringEqual(app_slug, APP_SLUGS.PAOBC)) {
    dispatch(fetchTranslations(`${app_slug}`, `${lang}`) as any);
    return;
  }
  dispatch(fetchTranslations(`${app_slug}`, `${country}`) as any);
}

function setTranslations(translations: any) {
  let { country } = decodeDataFromURL();
  const { lang } = decodeDataFromURL();
  if (lang) {
    if (translations) {
      const resources = translations;
      i18n.addResourceBundle("en", "translation", resources?.en?.translation, true, true);
      i18n.addResourceBundle(lang, "translation", resources?.[lang]?.translation, true, true);
      i18n.changeLanguage(lang);
    }
    return;
  }
  country = country?.toUpperCase() ?? "";
  if (translations) {
    const resources = translations;
    i18n.addResourceBundle("en", "translation", resources?.en?.translation, true, true);
    i18n.addResourceBundle(country, "translation", resources?.[country]?.translation, true, true);
    i18n.changeLanguage(country);
  }
}

function handleAdvertisementFetching(dispatch: any) {
  const { app_slug, country, showAds } = decodeDataFromURL();
  if (showAds) dispatch(fetchAdvertisements(`${app_slug}`, `${country}`) as any);
}

function fetchEthereumData(application: any) {
  const baseURL = "https://c2c.go48.co/api/eth";
  if (application?.slug === "goamalabs") {
    axios
      .get(baseURL)
      .then((response: any) => localStorage?.setItem(`ethereum`, response.data.eth))
      .catch(e => console.error(e));
  }
}

function handleGuestUserLoginPopup(isGuestUser: boolean, dispatch: any, application: any) {
  if (isGuestUser && application) {
    dispatch(fetchLoginPopCounter() as any);
  }
}

function handleSocialMediaAuth(user: any) {
  if (!user) return;
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  const userId = params.get("state");
  const socialAuth = params.get("social_login");

  if (socialAuth !== "apple") {
    localStorage.removeItem("apple-auth");
    sessionStorage.removeItem("connected-apple");
  } else if (userId === localStorage.getItem("apple-auth")) {
    sessionStorage.setItem("connected-apple", "apple");
    localStorage.removeItem("apple-auth");
  }
}

function adjustUILayout(application: any, dispatch: any) {
  dispatch(showThemeLayout({ layoutType: application?.ui_customization?.layout_type }));
}

function handleLeaderboardPopup(popUp: any, gamelistpopup: any, dispatch: any) {
  if (gamelistpopup) {
    dispatch(toggleGameListPopup());
  }

  if (popUp) {
    dispatch(hidePopUpAll());
  }
}

function handleLeaderboardPopupLocation(location: any, dispatch: any) {
  if (location.pathname !== "/tournament") {
    dispatch(hideLeaderboardPopup());
  }
}

function handleReferral({ isGuestUser = false, application, user }) {
  if (!application || !user) return;
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  let code = params.get("referral");
  if (!isGuestUser) {
    if (!code) {
      code = localStorage.getItem("referral");
    }
    if (code) {
      localStorage.setItem("referral_status", "pending");
      api
        .get(`referral/add-referral/${code.trim().replaceAll(`"`, "")}/${application?.slug}/`)
        .then(response => {
          store.dispatch(fetchBalance());
          localStorage.removeItem("referral");
          setTimeout(() => {
            localStorage.setItem("referral_status", JSON.stringify("redeemed"));
          }, 500);
          const { bonus_amount } = response.data || "";
          setTimeout(() => {
            store.dispatch(showRefferalRedeenSuccessPopUp({ amount: bonus_amount }));
          }, 2000);
        })
        .catch(e => {
          console.error(e);
          localStorage.removeItem("referral");
        });
    }
  } else {
    code && localStorage && localStorage.setItem("referral", JSON.stringify(code));
  }
}

function handleIOS() {
  window.getSelection()?.removeAllRanges();
}

function preventHorizontalScrollOnTouch() {
  let startX = 0;
  let startY = 0;

  const handleTouchStart = (event: TouchEvent) => {
    startX = event.touches[0].clientX;
    startY = event.touches[0].clientY;
  };

  const handleTouchMove = (event: TouchEvent) => {
    const deltaX = event.touches[0].clientX - startX;
    const deltaY = event.touches[0].clientY - startY;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      if (isKlook) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  };

  document.addEventListener("touchstart", handleTouchStart, { passive: true });
  document.addEventListener("touchmove", handleTouchMove, { passive: false });
  return { handleTouchStart, handleTouchMove };
}
